'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minHeight?: number;
  maxHeight?: number;
  onHeightChange?: (height: number) => void;
}

export const AutoResizeTextarea = React.forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextareaProps
>(
  (
    {
      className,
      minHeight = 40,
      maxHeight,
      onHeightChange,
      onChange,
      ...props
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isComposing, setIsComposing] = useState(false);

    // 合并 ref
    const mergedRef = useCallback(
      (node: HTMLTextAreaElement) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    // 调整高度的核心函数
    const adjustHeight = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      // 重置高度以获取正确的 scrollHeight
      textarea.style.height = 'auto';

      // 计算新高度
      let newHeight = Math.max(textarea.scrollHeight, minHeight);

      // 应用最大高度限制
      if (maxHeight) {
        newHeight = Math.min(newHeight, maxHeight);
      }

      // 设置新高度
      textarea.style.height = `${newHeight}px`;

      // 如果内容超出最大高度，显示滚动条
      if (maxHeight && textarea.scrollHeight > maxHeight) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }

      // 触发高度变化回调
      onHeightChange?.(newHeight);
    }, [minHeight, maxHeight, onHeightChange]);

    // 处理输入变化
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        // 只在非组合输入状态下调整高度
        if (!isComposing) {
          adjustHeight();
        }
        onChange?.(e);
      },
      [isComposing, adjustHeight, onChange],
    );

    // 处理中文输入法开始组合
    const handleCompositionStart = useCallback(() => {
      setIsComposing(true);
    }, []);

    // 处理中文输入法结束组合
    const handleCompositionEnd = useCallback(
      (e: React.CompositionEvent<HTMLTextAreaElement>) => {
        setIsComposing(false);
        // 组合结束后立即调整高度
        setTimeout(adjustHeight, 0);
      },
      [adjustHeight],
    );

    // 初始化和响应外部值变化
    useEffect(() => {
      adjustHeight();
    }, [adjustHeight, props.value]);

    // 监听窗口大小变化
    useEffect(() => {
      const handleResize = () => {
        setTimeout(adjustHeight, 0);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [adjustHeight]);

    return (
      <textarea
        ref={mergedRef}
        className={cn(
          // 基础样式
          'flex w-full rounded-lg border border-input text-foreground bg-background px-3 py-2 text-base ring-offset-background',
          // 占位符样式
          'placeholder:text-muted-foreground',
          // 焦点样式
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          // 禁用状态
          'disabled:cursor-not-allowed disabled:opacity-50',
          // 移除默认的 resize 控制
          'resize-none',
          // 自定义滚动条样式
          'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border',
          className,
        )}
        style={{
          minHeight: `${minHeight}px`,
          height: `${minHeight}px`,
          overflowY: 'hidden',
          // 确保行高一致性
          lineHeight: '1.5',
          // 优化中文字体渲染
          fontFamily:
            'var(--font-sans), "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
        }}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        {...props}
      />
    );
  },
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';
